import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../supabase';
import { useActiveOrg } from '../activeOrg';

export type SettingsOrganizationStatus =
  | 'loading'
  | 'ready'
  | 'forbidden'
  | 'unauth'
  | 'empty'
  | 'error';

export type ViewerRoleSystemKey = 'owner' | 'admin' | 'member' | null;

export type SaveRenameStatus = 'idle' | 'saving' | 'success' | 'error';

export type SaveRenameError = 'invalid_name' | 'name_taken' | 'network';

export type DeleteOrgStatus = 'idle' | 'deleting' | 'success' | 'error';

export type DeleteOrgError = 'name_mismatch' | 'network';

export const ORG_NAME_PATTERN = /^[A-Za-z0-9 -]{2,40}$/;
export const RESERVED_ORG_NAMES = new Set([
  'penrose',
  'admin',
  'system',
  'support'
]);

const SUCCESS_FLASH_MS = 4000;

export interface UseSettingsOrganizationVM {
  status: SettingsOrganizationStatus;
  /** Org id for the active (home) org. Null until the membership load resolves. */
  orgId: string | null;
  /** Current org name (server truth — updated optimistically after a saveRename
   *  success). */
  orgName: string | null;
  /** Viewer's permission_role.system_key in the active org. Drives the
   *  forbidden surface (Admin / Member / null → forbidden). */
  viewerRole: ViewerRoleSystemKey;
  /** Save-rename sub-state. `idle` → before save / after success-flash;
   *  `saving` → rpc in flight; `success` → 4s confirmation flash; `error` →
   *  inline errorText on the TextField. */
  saveRenameStatus: SaveRenameStatus;
  /** Discriminated error kind for the rename action. `invalid_name` is a
   *  client-side validation hit (length / character set / reserved token);
   *  `name_taken` is the server's `{ok:false, error:'name_taken'}` envelope;
   *  `network` covers transport / unexpected-shape failures. */
  saveRenameError: SaveRenameError | null;
  /** Delete-org sub-state. `idle` → before delete; `deleting` → rpc in flight;
   *  `success` → archive succeeded, the View shows the success modal; `error`
   *  → inline error inside the confirm panel. */
  deleteOrgStatus: DeleteOrgStatus;
  /** Discriminated error kind for the delete action. `name_mismatch` is the
   *  server's `{ok:false, error:'name_mismatch'}` envelope (the UI also gates
   *  the hold button on a client-side name match); `network` covers transport
   *  / unexpected-shape failures. */
  deleteOrgError: DeleteOrgError | null;
  /** Org name captured at delete time — drives the success modal title even
   *  after `orgName` would otherwise be considered stale. */
  deletedOrgName: string | null;
  /** Top-level load error message (shown via a Banner). */
  error: string | null;
  actions: {
    /** Run the rename. Returns true on `{ok:true}` (including no-op), false
     *  otherwise. Updates internal state for the View. */
    saveRename: (nextName: string) => Promise<boolean>;
    /** Clear the inline error (called when the user edits the field after a
     *  failed save so the errorText doesn't linger). */
    clearError: () => void;
    /** Archive (soft-delete) the org. `confirmName` must match `orgName`
     *  (case-sensitive, whitespace-tolerant). On `{ok:true}` fires the
     *  `send-org-deleted-email` Edge Function best-effort and flips the status
     *  to `success`. Returns true on success, false otherwise. */
    deleteOrg: (confirmName: string) => Promise<boolean>;
    /** Clear the delete-org inline error (called when the user edits the
     *  confirm-name field after a failed archive). */
    clearDeleteError: () => void;
  };
}

interface RenameOkBody {
  ok: true;
  name: string;
  noop?: boolean;
}
interface RenameErrBody {
  ok: false;
  error: 'invalid_name' | 'name_taken';
}
type RenameBody = RenameOkBody | RenameErrBody;

function isRenameBody(v: unknown): v is RenameBody {
  if (!v || typeof v !== 'object') return false;
  const o = v as { ok?: unknown; name?: unknown; error?: unknown };
  if (o.ok === true) return typeof o.name === 'string';
  if (o.ok === false) {
    return o.error === 'invalid_name' || o.error === 'name_taken';
  }
  return false;
}

interface DeleteOkBody {
  ok: true;
  org_name?: string;
  members_at_delete?: number;
  restorable_until?: string;
}
interface DeleteErrBody {
  ok: false;
  error: 'name_mismatch';
}
type DeleteBody = DeleteOkBody | DeleteErrBody;

function isDeleteBody(v: unknown): v is DeleteBody {
  if (!v || typeof v !== 'object') return false;
  const o = v as { ok?: unknown; error?: unknown };
  if (o.ok === true) return true;
  if (o.ok === false) return o.error === 'name_mismatch';
  return false;
}

function validateLocal(trimmed: string): SaveRenameError | null {
  if (!ORG_NAME_PATTERN.test(trimmed)) return 'invalid_name';
  if (RESERVED_ORG_NAMES.has(trimmed.toLowerCase())) return 'invalid_name';
  return null;
}

/**
 * ViewModel for `/settings/organization` (turn 4.2b). Loads the caller's
 * home org (id + name) and viewer permission_role.system_key using the same
 * "oldest membership wins" heuristic as `useSettingsOverviewVM` /
 * `useMembersVM` (until a shared active-org context lands). When the viewer
 * isn't Owner the VM short-circuits to `forbidden` — the View renders a 403
 * surface and never mounts the Rename form.
 *
 * `saveRename` calls `public.rename_org(p_org_id, p_new_name)` (see
 * `penrose-supabase` migration `20260514032713_rename_org_rpc.sql`) and
 * maps the jsonb envelope back to view state:
 *   - `{ok:true, name}`                → optimistic name update + success flash
 *     + cross-tab BroadcastChannel('penrose-org-changed').
 *   - `{ok:true, name, noop:true}`     → same path; the flash + broadcast still
 *     fire so the user gets feedback (the server already short-circuited the
 *     UPDATE).
 *   - `{ok:false, error:'name_taken'}` → inline TextField errorText.
 *   - `{ok:false, error:'invalid_name'}` → inline TextField errorText (server
 *     defence-in-depth; we also validate client-side before dispatching).
 *   - Network / unexpected shape         → `network` error.
 */
export function useSettingsOrganizationVM(): UseSettingsOrganizationVM {
  const { activeOrgId, activeOrgName } = useActiveOrg();
  const [status, setStatus] = useState<SettingsOrganizationStatus>('loading');
  const orgId = activeOrgId;
  // Local optimistic override of the org name. Seeded from the shared
  // active-org context in the mount/resolve effect and bumped on a successful
  // rename so the success flash / delete-confirm match see the new name before
  // the context round-trips.
  const [orgName, setOrgName] = useState<string | null>(null);
  const [viewerRole, setViewerRole] = useState<ViewerRoleSystemKey>(null);
  const [error, setError] = useState<string | null>(null);
  const [saveRenameStatus, setSaveRenameStatus] =
    useState<SaveRenameStatus>('idle');
  const [saveRenameError, setSaveRenameError] =
    useState<SaveRenameError | null>(null);
  const [deleteOrgStatus, setDeleteOrgStatus] =
    useState<DeleteOrgStatus>('idle');
  const [deleteOrgError, setDeleteOrgError] =
    useState<DeleteOrgError | null>(null);
  const [deletedOrgName, setDeletedOrgName] = useState<string | null>(null);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      if (cancelled) return;
      const session = sessionData.session;
      if (!session) {
        setStatus('unauth');
        return;
      }
      // Stay in `loading` until the shared shell resolves the active org.
      if (!activeOrgId) return;

      const { data: membershipRow, error: memErr } = await supabase
        .from('memberships')
        .select('permission_role:permission_role_id(system_key)')
        .eq('user_id', session.user.id)
        .eq('org_id', activeOrgId)
        .maybeSingle();

      if (cancelled) return;
      if (memErr) {
        setStatus('error');
        setError("Couldn't load your organization. Try again.");
        return;
      }

      type RoleEmbed =
        | { system_key?: string | null }
        | { system_key?: string | null }[]
        | null;
      const row = (membershipRow ?? null) as {
        permission_role: RoleEmbed;
      } | null;
      if (!row) {
        setStatus('empty');
        return;
      }
      const sysKey = Array.isArray(row.permission_role)
        ? row.permission_role[0]?.system_key ?? null
        : row.permission_role?.system_key ?? null;
      const role: ViewerRoleSystemKey =
        sysKey === 'owner' || sysKey === 'admin' || sysKey === 'member'
          ? sysKey
          : null;

      setOrgName(activeOrgName);
      setViewerRole(role);
      setStatus(role === 'owner' ? 'ready' : 'forbidden');
    })();
    return () => {
      cancelled = true;
    };
  }, [activeOrgId, activeOrgName]);

  useEffect(
    () => () => {
      if (flashTimer.current) clearTimeout(flashTimer.current);
    },
    []
  );

  const clearError = useCallback(() => {
    setSaveRenameError(null);
    setSaveRenameStatus((s) => (s === 'error' ? 'idle' : s));
  }, []);

  const saveRename = useCallback(
    async (nextName: string): Promise<boolean> => {
      if (!orgId) return false;
      const trimmed = nextName.trim();
      const local = validateLocal(trimmed);
      if (local) {
        setSaveRenameError(local);
        setSaveRenameStatus('error');
        return false;
      }

      setSaveRenameStatus('saving');
      setSaveRenameError(null);

      const { data, error: rpcErr } = await supabase.rpc('rename_org', {
        p_org_id: orgId,
        p_new_name: trimmed
      });

      if (rpcErr || !isRenameBody(data)) {
        setSaveRenameStatus('error');
        setSaveRenameError('network');
        return false;
      }

      if (data.ok) {
        setOrgName(data.name);
        setSaveRenameStatus('success');
        setSaveRenameError(null);
        try {
          if (typeof BroadcastChannel !== 'undefined') {
            const ch = new BroadcastChannel('penrose-org-changed');
            ch.postMessage({
              kind: 'renamed',
              orgId,
              name: data.name
            });
            ch.close();
          }
        } catch {
          // BroadcastChannel may be unavailable (older browsers, test envs);
          // the rename still succeeded.
        }
        if (flashTimer.current) clearTimeout(flashTimer.current);
        flashTimer.current = setTimeout(() => {
          setSaveRenameStatus((s) => (s === 'success' ? 'idle' : s));
        }, SUCCESS_FLASH_MS);
        return true;
      }

      setSaveRenameStatus('error');
      setSaveRenameError(data.error);
      return false;
    },
    [orgId]
  );

  const clearDeleteError = useCallback(() => {
    setDeleteOrgError(null);
    setDeleteOrgStatus((s) => (s === 'error' ? 'idle' : s));
  }, []);

  const deleteOrg = useCallback(
    async (confirmName: string): Promise<boolean> => {
      if (!orgId || !orgName) return false;
      const trimmed = confirmName.trim();
      // Defense-in-depth: the View gates the hold button on this match, but a
      // smuggled call still gets refused before the rpc fires.
      if (trimmed !== orgName) {
        setDeleteOrgError('name_mismatch');
        setDeleteOrgStatus('error');
        return false;
      }

      setDeleteOrgStatus('deleting');
      setDeleteOrgError(null);

      const { data, error: rpcErr } = await supabase.rpc('delete_org', {
        p_org_id: orgId,
        p_confirm_name: trimmed
      });

      if (rpcErr || !isDeleteBody(data)) {
        setDeleteOrgStatus('error');
        setDeleteOrgError('network');
        return false;
      }

      if (!data.ok) {
        setDeleteOrgStatus('error');
        setDeleteOrgError(data.error === 'name_mismatch' ? 'name_mismatch' : 'network');
        return false;
      }

      // Best-effort member-notification blast. Failures (incl. a missing
      // RESEND_API_KEY on the branch) must not block the archive — the Edge
      // Function itself is best-effort and returns HTTP 200 on partial sends.
      try {
        await supabase.functions.invoke('send-org-deleted-email', {
          body: { orgId, orgName }
        });
      } catch {
        // Swallowed — surfaced in support tooling, never in the user UI.
      }

      setDeletedOrgName(data.org_name ?? orgName);
      setDeleteOrgStatus('success');
      setDeleteOrgError(null);
      return true;
    },
    [orgId, orgName]
  );

  return {
    status,
    orgId,
    orgName,
    viewerRole,
    saveRenameStatus,
    saveRenameError,
    deleteOrgStatus,
    deleteOrgError,
    deletedOrgName,
    error,
    actions: { saveRename, clearError, deleteOrg, clearDeleteError }
  };
}
